import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { Confirmer, CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { Notifier, NOTIFIER } from '@shared/notifier/types';
import { DataTableHeader } from '@shared/tables/components/e-simple-data-table/e-simple-data-table.component';
import { PlatformUserAccountDto } from '@data/_generated-api/platform-api/models/platform-user-account-dto';
import { AccountAdminApiService } from '../../services/account-admin-api.service';

/**
 * Admin dashboard voor accountbeheer (EGI-38): accountoverzicht met beheeracties
 * (verifiëren, reset-mail, force logout, deactiveren).
 */
@Component({
  selector: 'account-management-view',
  imports: [FormsModule, TranslocoModule, SharedModule],
  templateUrl: './account-management-view.component.html',
  styleUrl: './account-management-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountManagementViewComponent implements OnInit {
  private readonly adminApi = inject(AccountAdminApiService);
  private readonly confirmer = inject<Confirmer>(CONFIRMER);
  private readonly notifier = inject<Notifier>(NOTIFIER);
  private readonly transloco = inject(TranslocoService);

  protected readonly accounts = signal<PlatformUserAccountDto[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly onlineCount = computed(
    () => this.accounts().filter(account => (account.activeSessionCount ?? 0) > 0).length
  );

  protected readonly headers: DataTableHeader<PlatformUserAccountDto>[] = [
    { key: 'email', label: this.t('columns.email') },
    { key: 'displayName', label: this.t('columns.name') },
    { key: 'isVerified', label: this.t('columns.verified') },
    { key: 'lastLoginOn', label: this.t('columns.lastLogin') },
    { key: 'activeSessionCount', label: this.t('columns.sessions') },
    { key: 'createdOn', label: this.t('columns.createdOn') },
    { key: 'accountCreatedByMethod', label: this.t('columns.origin') }
  ];

  ngOnInit(): void {
    this.loadAccounts();
  }

  protected async loadAccounts(): Promise<void> {
    this.isLoading.set(true);
    const result = await this.adminApi.getAllAccounts();
    this.isLoading.set(false);
    result.ifSuccess(accounts => this.accounts.set(accounts));
    result.ifError(() => this.notifier.error(this.t('loadFailed')));
  }

  protected async onDeactivate(account: PlatformUserAccountDto): Promise<void> {
    const confirmed = await this.confirmer.confirm(this.t('confirmDeactivate'), { color: 'danger' });
    if (!confirmed) {
      return;
    }
    const result = await this.adminApi.deactivate(account.id);
    result.ifSuccess(() => {
      this.notifier.success(this.t('deactivated'));
      this.loadAccounts();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onReactivate(account: PlatformUserAccountDto): Promise<void> {
    const result = await this.adminApi.reactivate(account.id);
    result.ifSuccess(() => {
      this.notifier.success(this.t('reactivated'));
      this.loadAccounts();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onAdminVerify(account: PlatformUserAccountDto): Promise<void> {
    const result = await this.adminApi.adminVerify(account.id);
    result.ifSuccess(() => {
      this.notifier.success(this.t('verified'));
      this.loadAccounts();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onTriggerPasswordReset(account: PlatformUserAccountDto): Promise<void> {
    const result = await this.adminApi.triggerPasswordReset(account.id, this.transloco.getActiveLang());
    result.ifSuccess(() => this.notifier.success(this.t('resetSent')));
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onForceLogout(account: PlatformUserAccountDto): Promise<void> {
    const confirmed = await this.confirmer.confirm(this.t('confirmForceLogout'), { color: 'danger' });
    if (!confirmed) {
      return;
    }
    const result = await this.adminApi.forceLogout(account.id);
    result.ifSuccess(() => {
      this.notifier.success(this.t('loggedOut'));
      this.loadAccounts();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  private t(key: string): string {
    return this.transloco.translate(`account.admin.${key}`);
  }
}
